/**
 * CAOS-590 — the breakpoint axis: three named widths that every component and every page author
 * shares.
 *
 * CAOS-586 decided the axis (the design-axes decision, Axis 2). Components had written five widths
 * by hand — 480, 560, 720, 1100 and 1280, all upper bounds — and read for intent those were three
 * decisions: stack what sat side by side, compact the surface, and trim what is optional. Three
 * steps, named the way the incumbent's layout item names its sizes. This file is the only place a
 * width lives.
 *
 * ── WHY THIS IS NOT A `--caos-*` TOKEN ──────────────────────────────────────
 * A custom property cannot be read inside a media query. `@media (min-width: var(--x))` is not
 * valid CSS and never matches, with no error anywhere. A breakpoint token would be a name nothing
 * could use. So the scale is declared here in TypeScript, and a component writes
 * `${atLeast('medium')}` into the stylesheet it already builds from a string.
 * `test/breakpoints.test.ts` refuses a raw pixel width in any component's media query, so the scale
 * cannot quietly be bypassed again.
 *
 * ── WHAT A NAME MEANS ────────────────────────────────────────────────────────
 * Each name applies AT AND ABOVE its width. A layout item's `mediumSize` holds from 720px up until
 * a `largeSize` takes over, and below `small` an item takes its base `size`.
 *
 * Consolidating moved three widths. 480 and 560 became 520, each moving 40px. 1100 and 1280 became
 * 1200, moving 100px and 80px. 720 did not move. Each component that moved was checked by eye at
 * its new width rather than assumed unaffected.
 */
export const BREAKPOINTS = {
  small: 520,
  medium: 720,
  large: 1200,
} as const;

export type BreakpointName = keyof typeof BREAKPOINTS;

/** The names in ascending width — the order a per-breakpoint size is resolved in. */
export const BREAKPOINT_NAMES: readonly BreakpointName[] = ['small', 'medium', 'large'];

/** A media condition that holds at and above the named width, e.g. `(min-width: 720px)`. */
export function atLeast(name: BreakpointName): string {
  return `(min-width: ${BREAKPOINTS[name]}px)`;
}

/**
 * A media condition that holds below the named width, e.g. `(max-width: 719.98px)`.
 *
 * The .02 keeps `below('medium')` and `atLeast('medium')` from both holding at exactly 720px, which
 * a plain `max-width: 720px` would do. Without it, a component that stacks below a width and a
 * layout that splits at that width would disagree for one pixel.
 */
export function below(name: BreakpointName): string {
  return `(max-width: ${BREAKPOINTS[name] - 0.02}px)`;
}
